import { useMemo, useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatPhone } from '@/lib/formatters'
import { cn } from '@/lib/utils'
import {
  LeadTemperature,
  PipelineSection,
  temperatureLabels,
  type WarmAccount,
  type WarmStats,
} from '@/models/myPipeline'
import { salesLeadStatusColors, salesLeadStatusLabels } from '@/models/salesLead'
import { InlineEditField } from '../SalesLeads/InlineEditField'
import { AddToPipelineDialog } from './AddToPipelineDialog'
import { PipelineRowMenu } from './PipelineRowMenu'
import { TemperatureChip } from './TemperatureChip'

const PAGE_SIZE = 25
const STALE_DAYS = 14
const AGEING_DAYS = 7

enum WarmSort {
  TEMPERATURE = 'TEMPERATURE',
  LAST_TOUCH = 'LAST_TOUCH',
  SCORE = 'SCORE',
  NAME = 'NAME',
}

const SORT_LABELS: Record<WarmSort, string> = {
  [WarmSort.TEMPERATURE]: 'Hottest first',
  [WarmSort.LAST_TOUCH]: 'Longest since touch',
  [WarmSort.SCORE]: 'Highest score',
  [WarmSort.NAME]: 'Account name',
}

const COLUMNS = ['Account', 'Primary contact', 'Status', 'Temp', 'Score', 'Last touch', 'Next step', '']

function lastTouchClass(days: number | null): string {
  if (days === null) return 'text-muted-foreground'
  if (days > STALE_DAYS) return 'text-red-600 font-medium'
  if (days > AGEING_DAYS) return 'text-orange-600 font-medium'
  return 'text-muted-foreground'
}

function scoreClass(score: number): string {
  if (score >= 80) return 'bg-green-100 text-green-700'
  if (score >= 50) return 'bg-amber-100 text-amber-700'
  return 'bg-slate-100 text-slate-700'
}

function ScoreCell({ score }: { score: number | null }) {
  if (score === null) return <span className="text-muted-foreground text-sm">—</span>
  return (
    <span
      className={cn('inline-flex rounded-full px-2 py-0.5 text-xs font-semibold', scoreClass(score))}
      aria-label={`Conversion score ${score} of 100`}
    >
      {score}
    </span>
  )
}

function sortRows(rows: WarmAccount[], sort: WarmSort): WarmAccount[] {
  const copy = [...rows]
  switch (sort) {
    case WarmSort.LAST_TOUCH:
      return copy.sort((a, b) => (b.daysSinceTouch ?? -1) - (a.daysSinceTouch ?? -1))
    case WarmSort.SCORE:
      return copy.sort((a, b) => (b.conversionScore ?? -1) - (a.conversionScore ?? -1))
    case WarmSort.NAME:
      return copy.sort((a, b) => a.legalName.localeCompare(b.legalName))
    default:
      // The backend already returns hottest-then-stalest order.
      return copy
  }
}

function CountCard({
  label,
  value,
  valueClassName,
}: {
  label: string
  value: number
  valueClassName?: string
}) {
  return (
    <Card>
      <CardContent>
        <div className="text-muted-foreground text-sm">{label}</div>
        <div className={cn('mt-2 text-2xl font-bold tabular-nums', valueClassName)}>{value}</div>
      </CardContent>
    </Card>
  )
}

function AccountCell({ account }: { account: WarmAccount }) {
  const subline = [
    account.dotNumber ? `DOT ${account.dotNumber}` : null,
    account.powerUnits ? `${account.powerUnits} units` : null,
    account.state || null,
    account.pipelineSection === PipelineSection.AUTO ? 'auto' : null,
  ]
    .filter(Boolean)
    .join(' · ')
  return (
    <div>
      <div className="font-medium">{account.legalName}</div>
      <div className="text-muted-foreground text-xs">{subline || '—'}</div>
    </div>
  )
}

function StatusCell({ account }: { account: WarmAccount }) {
  return (
    <div>
      <Badge className={salesLeadStatusColors[account.status]}>{salesLeadStatusLabels[account.status]}</Badge>
      {account.isUnresponsive && (
        <div className="text-xs font-medium text-red-600">Unresponsive · 3 calls</div>
      )}
      {account.sequenceRunning && <div className="text-muted-foreground text-xs">Sequence running</div>}
    </div>
  )
}

interface WarmAccountsTableProps {
  accounts: WarmAccount[]
  stats: WarmStats
  repId: string
  /** The viewed month, "yyyy-MM-01". */
  month: string
}

export function WarmAccountsTable({ accounts, stats, repId, month }: WarmAccountsTableProps) {
  const [search, setSearch] = useState('')
  const [temperature, setTemperature] = useState('all')
  const [sort, setSort] = useState<WarmSort>(WarmSort.TEMPERATURE)
  const [showAll, setShowAll] = useState(false)

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase()
    const matches = accounts.filter((account) => {
      if (temperature !== 'all' && account.temperature !== temperature) return false
      if (!query) return true
      return (
        account.legalName.toLowerCase().includes(query) ||
        account.dbaName.toLowerCase().includes(query) ||
        (account.dotNumber ?? '').includes(query)
      )
    })
    return sortRows(matches, sort)
  }, [accounts, search, temperature, sort])

  const visible = showAll ? filtered : filtered.slice(0, PAGE_SIZE)

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">Warm accounts</h2>
        <AddToPipelineDialog repId={repId} section={PipelineSection.WARM} month={month} />
      </div>

      <div className="grid grid-cols-3 gap-3">
        <CountCard label="Total accounts" value={stats.total} />
        <CountCard label="Hot leads" value={stats.hot} valueClassName="text-red-600" />
        <CountCard label="Follow-ups due" value={stats.followUpsDue} />
      </div>

      <div className="flex flex-wrap gap-2">
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search accounts"
          className="max-w-xs"
        />
        <Select value={temperature} onValueChange={setTemperature}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Temperature" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All temperatures</SelectItem>
            {Object.values(LeadTemperature).map((value) => (
              <SelectItem key={value} value={value}>
                {temperatureLabels[value]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={(value) => setSort(value as WarmSort)}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            {Object.values(WarmSort).map((value) => (
              <SelectItem key={value} value={value}>
                {SORT_LABELS[value]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {visible.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center">
          <p className="text-muted-foreground text-sm">No warm accounts match these filters.</p>
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col gap-3">
            <Table>
              <TableHeader>
                <TableRow>
                  {COLUMNS.map((column) => (
                    <TableHead key={column} className="font-semibold">
                      {column}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.map((account) => (
                  <TableRow key={account.id}>
                    <TableCell>
                      <AccountCell account={account} />
                    </TableCell>
                    <TableCell className="text-sm">
                      <div>{formatPhone(account.primaryContact?.phone) || '—'}</div>
                      <div className="text-muted-foreground text-xs">
                        {account.primaryContact?.email || '—'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusCell account={account} />
                    </TableCell>
                    <TableCell>
                      <TemperatureChip leadId={account.id} temperature={account.temperature} />
                    </TableCell>
                    <TableCell>
                      <ScoreCell score={account.conversionScore} />
                    </TableCell>
                    <TableCell className={cn('text-sm', lastTouchClass(account.daysSinceTouch))}>
                      {account.daysSinceTouch === null ? 'Never' : `${account.daysSinceTouch}d`}
                    </TableCell>
                    <TableCell>
                      <InlineEditField
                        leadId={account.id}
                        field="nextAction"
                        value={account.nextStep}
                        placeholder="Add next step"
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <PipelineRowMenu
                        leadId={account.id}
                        legalName={account.legalName}
                        shownAs={PipelineSection.WARM}
                        pipelineSection={account.pipelineSection}
                        month={month}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {!showAll && filtered.length > PAGE_SIZE && (
              <Button variant="outline" onClick={() => setShowAll(true)}>
                Show all {filtered.length}
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
